// src/components/employee/TimeOffRequest.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  TextField,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  BeachAccess as TimeOffIcon,
  Add as AddIcon,
  Pending as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as DeniedIcon,
} from '@mui/icons-material';
import EmployeeMobileLayout from './EmployeeMobileLayout';
import { useEmployeeData } from '../../hooks/useEmployeeData';

export default function TimeOffRequest({ employee }) {
  const { timeOffRequests, submitTimeOffRequest } = useEmployeeData(employee?.id);
  const [requests, setRequests] = useState([]);
  const [requestDialog, setRequestDialog] = useState(false);
  const [newRequest, setNewRequest] = useState({
    startDate: '',
    endDate: '',
    type: '',
    reason: ''
  });

  useEffect(() => {
    if (timeOffRequests.length > 0) {
      setRequests(timeOffRequests);
    } else {
      // Mock data when no real data available
      setRequests([
        {
          id: 1,
          startDate: '2025-08-15',
          endDate: '2025-08-17',
          type: 'Vacation',
          reason: 'Family trip to the beach',
          status: 'pending',
          submittedAt: '2025-07-20',
          reviewedBy: null,
          reviewedAt: null,
          managerNotes: ''
        },
        {
          id: 2,
          startDate: '2025-07-30',
          endDate: '2025-07-30',
          type: 'Personal',
          reason: 'Doctor appointment',
          status: 'approved',
          submittedAt: '2025-07-15',
          reviewedBy: 'Sarah Manager',
          reviewedAt: '2025-07-16',
          managerNotes: 'Approved - have a good appointment!'
        },
        {
          id: 3,
          startDate: '2025-07-10',
          endDate: '2025-07-12',
          type: 'Sick',
          reason: 'Flu symptoms',
          status: 'approved',
          submittedAt: '2025-07-09',
          reviewedBy: 'Mike Supervisor',
          reviewedAt: '2025-07-09',
          managerNotes: 'Feel better soon!'
        }
      ]);
    }
  }, [timeOffRequests]);

  const handleSubmitRequest = async () => {
    if (!newRequest.startDate || !newRequest.endDate || !newRequest.type) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      if (employee?.id) {
        // Try to submit to Firebase
        await submitTimeOffRequest(newRequest);
      } else {
        // Fallback to mock data
        const request = {
          ...newRequest,
          id: Date.now(),
          status: 'pending',
          submittedAt: new Date().toISOString().split('T')[0]
        };
        setRequests(prev => [request, ...prev]);
      }

      setRequestDialog(false);
      setNewRequest({ startDate: '', endDate: '', type: '', reason: '' });
      alert('Time off request submitted successfully!');
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Failed to submit request. Please try again.');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <PendingIcon sx={{ color: 'warning.main' }} />;
      case 'approved':
        return <ApprovedIcon sx={{ color: 'success.main' }} />;
      case 'denied':
        return <DeniedIcon sx={{ color: 'error.main' }} />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'denied':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  return (
    <EmployeeMobileLayout title="Time Off Requests" employee={employee}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              My Time Off
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {requests.length} requests total
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setRequestDialog(true)}
          >
            New Request
          </Button>
        </Box>

        {/* Requests List */}
        <Stack spacing={2}>
          {requests.map((request) => (
            <Card key={request.id} elevation={2}>
              <CardContent>
                {/* Header with Status */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {formatDate(request.startDate)}
                      {request.startDate !== request.endDate && (
                        <> - {formatDate(request.endDate)}</>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {calculateDays(request.startDate, request.endDate)} day{calculateDays(request.startDate, request.endDate) > 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Chip
                    icon={getStatusIcon(request.status)}
                    label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    color={getStatusColor(request.status)}
                    size="small"
                  />
                </Box>

                {/* Type and Reason */}
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip label={request.type} variant="outlined" size="small" />
                </Stack>

                {request.reason && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Reason:</strong> {request.reason}
                    </Typography>
                  </Box>
                )}

                {/* Manager Response */}
                {request.status !== 'pending' && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: request.status === 'approved' ? 'success.50' : 'error.50', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>{request.status === 'approved' ? 'Approved' : 'Denied'} by {request.reviewedBy}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      {formatDate(request.reviewedAt)}
                    </Typography>
                    {request.managerNotes && (
                      <Typography variant="body2">
                        {request.managerNotes}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Submission Info */}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Submitted on {formatDate(request.submittedAt)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {/* Empty State */}
        {requests.length === 0 && (
          <Card elevation={1}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <TimeOffIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No Time Off Requests
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Submit your first time off request
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* New Request Dialog */}
      <Dialog 
        open={requestDialog} 
        onClose={() => setRequestDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New Time Off Request</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              Submit your request at least 2 weeks in advance for best approval chances.
            </Alert>

            <TextField
              label="Start Date"
              type="date"
              fullWidth
              value={newRequest.startDate}
              onChange={(e) => setNewRequest(prev => ({ ...prev, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              label="End Date"
              type="date"
              fullWidth
              value={newRequest.endDate}
              onChange={(e) => setNewRequest(prev => ({ ...prev, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Type of Time Off</InputLabel>
              <Select
                value={newRequest.type}
                label="Type of Time Off"
                onChange={(e) => setNewRequest(prev => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="Vacation">Vacation</MenuItem>
                <MenuItem value="Personal">Personal Day</MenuItem>
                <MenuItem value="Sick">Sick Leave</MenuItem>
                <MenuItem value="Family">Family Emergency</MenuItem>
                <MenuItem value="Medical">Medical Appointment</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Reason (Optional)"
              multiline
              rows={3}
              fullWidth
              value={newRequest.reason}
              onChange={(e) => setNewRequest(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Brief description of your time off request"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitRequest}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </EmployeeMobileLayout>
  );
}
